import { AssetConfigSchema } from "appworks/config/asset-config-schema";
import { AnimatedSprite } from "appworks/graphics/animation/animated-sprite";
import { animationManager } from "appworks/graphics/animation/animation-manager";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { spineManager } from "appworks/graphics/spine/spine-manager";
import { asArray } from "appworks/utils/collection-utils";
import { Rectangle } from "appworks/utils/geom/rectangle";
import * as Logger from "js-logger";
import { Graphics, Texture } from "pixi.js";

declare const __DEBUG__: boolean;

class GraphicsManager {

    private textures: Map<string, Texture> = new Map<string, Texture>();

    /**
     * Call this after preloading.
     *
     * Creates a pixi renderer (WebGL with canvas fallback), an HTML canvas element and a pixi stage container
     * Uses graphics config to intialize factory methods
     * Starts render loop (requestAnimationFrame)
     *
     * @param config {GraphicsConfigSchema}
     */
    public init(config: AssetConfigSchema) {
        const animationConfig = config.layouts.all.animations;

/////////////////////////
////////////////////////
//////////////////////////////////////
/////////
//////////////////

        animationManager.init(animationConfig);
    }

    /**
     * Registers a texture with the graphics manager
     *
     * @param id {string}
     * @param texture {Texture}
     */
    public addTexture(id: string, texture: Texture) {
        this.textures.set(id, texture);
    }

    /**
     * Check for the existence of a texture
     * @param id {string}
     */
    public hasTexture(id: string): boolean {
        return this.textures.has(id);
    }

    /**
     * Returns a reference to a texture previously registered
     * Don't use this method for sprite creation, instead use createSprite factory method
     *
     * @param id {string}
     * @returns {Texture}
     */
    public getTexture(id: string): Texture {
        if (!this.hasTexture(id)) {
            throw new Error("Texture `" + id + "` does not exist in texture cache");
        }
        return this.textures.get(id);
    }

    public getTextureWithFallback(ids: string[]): Texture | null {
        while (ids.length) {
            const id = ids.shift();
            try {
                return this.getTexture(id);
            } catch (err) {
                //
            }
        }
    }

    /**
     * Destroys a texture and removes it from the cache
     *
     * @param id {string}
     * @param texture {Texture}
     */
    public destroyTexture(id: string) {
        const texture = this.getTexture(id);
        texture.destroy(true);

        this.textures.delete(id);
    }

    /**
     * Factory method to instantiate a new Sprite from a given ID
     * Note the textures it uses are from the pixi cache, but the sprite object itself is a new object in memory
     *
     * @param id {string}
     * @returns {Sprite}
     */
    public createSprite(id: string): Sprite;

    /**
     * Factory method to instantiate a new Sprite from a list of IDs, will keep trying until it finds one which exists, or return null
     * Note the textures it uses are from the pixi cache, but the sprite object itself is a new object in memory
     *
     * @param ids {string}
     * @returns {Sprite}
     */
    public createSprite(ids: string[]): Sprite;

    public createSprite(id: string | string[]): Sprite {
        let ids;
        if (typeof id === "string") {
            ids = [id];
        } else {
            ids = id;
        }

        let sprite = null;

        while (ids.length > 0) {
            const spriteId = ids.shift();

            if (!this.textures.has(spriteId)) {
                if (typeof id === "string") {
                    throw new Error("Invalid texture id: " + id);
                }
            } else {
                sprite = new Sprite(this.textures.get(spriteId));
            }
        }

        return sprite;
    }

    public createBlankSprite(): Sprite {
        return new Sprite(Texture.WHITE);
    }

    public createSpriteWithFallback(id: string | string[]): Sprite | null {
        try {
            return this.createSprite(asArray(id));
        } catch (err) {
            return null;
        }
    }

    /**
     * Creates an animation by name
     */
    public createAnimation(id: string): AnimatedSprite;

    /**
     * Goes through the list and returns the first valid animation
     */
    public createAnimation(ids: string[]): AnimatedSprite;

    /**
     * Factory method to instantiate a new AnimatedSprite.
     * Note the textures it uses are from the pixi cache, but the sprite object itself is a new object in memory
     */
    public createAnimation(ids: string | string[]): AnimatedSprite {

        let animation = null;

        if (typeof ids === "string") {
            animation = animationManager.generateAnimation(ids);
        } else {
            animation = animationManager.generateAnimationWithFallbacks(ids);
        }

        return animation;
    }

    public createSpine(name: string) {
        return spineManager.createSpine(name);
    }

    /**
     * Factory method to instantiate a new AnimatedSprite combining frames over multiple animations
     * Config (fps, loop etc) is taken from first in the list
     */
    public createMergedAnimation(ids: string[]): AnimatedSprite {
        let animation = null;

        animation = animationManager.generateAnimation(ids);

        return animation;
    }

    public createRectangle(rectangle: Rectangle, fillColor: number = 0, fillAlpha: number = 1, strokeThickness: number = 0, strokeColor: number = 0, strokeAlpha: number = 1) {
        const graphics = new Graphics();

        graphics.beginFill(fillColor, fillAlpha);
        if (strokeThickness > 0) {
            graphics.lineStyle(strokeThickness, strokeColor, strokeAlpha);
        }
        graphics.drawRect(rectangle.x, rectangle.y, rectangle.width, rectangle.height);
        graphics.endFill();

        return graphics;

    }

    private listAllFonts(config: AssetConfigSchema) {
        const fontDictionary = new Map<string, boolean>();

        Logger.info("Used fonts:");

        for (const layout of [config.layouts.desktop, config.layouts.mobile]) {
            if (layout.graphics) {
                for (const layerSceneName in layout.graphics) {
                    if (layout.graphics.hasOwnProperty(layerSceneName)) {
                        const layerScene = layout.graphics[layerSceneName];
                        if (layerScene.text) {
                            for (const textName in layerScene.text) {
                                if (layerScene.text.hasOwnProperty(textName)) {
                                    const text = layerScene.text[textName];
                                    if (text.landscape) {
                                        fontDictionary.set(text.landscape.fontFamily + "-" + text.landscape.fontVariant, true);
                                    }
                                    if (text.portrait) {
                                        fontDictionary.set(text.portrait.fontFamily + "-" + text.portrait.fontVariant, true);
                                    }
                                }
                            }
                        }
                    }
                }
            }
            fontDictionary.forEach((value, index) => Logger.info(index));
        }
    }
}

export const graphicsManager = new GraphicsManager();
